
// @TODO: Everything related to reviews should be moved outside the page

import { Component, namespace, Vue } from 'nuxt-property-decorator'
import { Context } from '@nuxt/types'
import type { MetaInfo } from 'vue-meta'
import { OnCreated, OnMounted, WithAsyncData, WithHead } from '~/core/vue.types'
import { OfferType, OfferTypeByShortName, OfferTypeKey, Provider, Review } from '~/core/offer.types'
import ProviderLayout from '~/views/providers/ProviderLayout.vue'
import ProviderCommonCard from '~/components/providers/ProviderCommonCard.vue'
import AboutProvider from '~/components/providers/AboutProvider.vue'
import ProviderContentArticle from '~/components/providers/ProviderContentArticle.vue'
import CheckAvailabilitySection from '~/components/providers/CheckAvailabilitySection.vue'
import ReviewsSection from '~/components/sections/rating/ReviewsSection.vue'
import ReviewsFilter from '~/components/sections/rating/ReviewsFilter.vue'
import ReviewsBox from '~/components/sections/rating/ReviewsBox.vue'
import ProviderRatingCarousel from '~/components/providers/ProviderRatingCarousel.vue'
import ProviderRatingCard from '~/components/providers/ProviderRatingCard.vue'
import { $api } from '~/plugins/axios'
import { namespace as offerStore, OfferState } from '~/store/modules/Offer'
import { namespace as mobileFiltersStore } from '~/store/modules/Amp/Filters/Mobile'
import { namespace as homeFiltersStore } from '~/store/modules/Amp/Filters/Home'
import SaveRouteMixin from '~/mixins/save-route.mixin'
import ProviderComparator from '~/views/comparator/ProviderComparator.vue'
import { useSEOMetaComposer } from '~/composables/seo.composable'
import { FiltersItemValue, AmpHomeFiltersState, AmpMobileFiltersState } from '~/core/config/filters/types'

interface AsyncComponentData {
  slug: string,
  provider: Provider,
  contentArticles: any[],
}

let debounce: any = null

const OfferStore = namespace(offerStore)

const HomeFiltersModule = namespace(mobileFiltersStore)
const MobileFiltersModule = namespace(homeFiltersStore)

@Component({
  nuxtI18n: {
    paths: {
      en: '/provider/:slug',
      de: '/anbieter/:slug',
      fr: '/operateur/:slug',
    },
  },
  components: {
    ProviderComparator,
    ProviderLayout,
    ProviderCommonCard,
    AboutProvider,
    ProviderContentArticle,
    CheckAvailabilitySection,
    ReviewsSection,
    ReviewsFilter,
    ReviewsBox,
    ProviderRatingCarousel,
    ProviderRatingCard,
  },
  mixins: [SaveRouteMixin],
  middleware: (ctx) => {
    if (ctx.params.slug === 'm-budget') {
      ctx.redirect(301, ctx.localePath({
        name: 'provider-slug', params: { slug: 'migros-mobile' },
      }))
    }
  },
})
export default class SpecificProvider extends Vue implements WithAsyncData, OnMounted, OnCreated, WithHead {
  @OfferStore.State('providersList')
  providersList!: OfferState['providersList']

  private slug!: string
  private provider!: Provider
  private contentArticles!: any[]
  private sidebarArticles!: any[]

  private offerType: OfferTypeKey = OfferTypeByShortName.mobile

  private reviewsPage = 1
  private reviewsCount = 0
  private reviewsFilterForm = {
    city: '',
    ageGroup: 0,
    rating: 0,
  }

  private isReviewsFullLoading = true
  private isReviewsPartLoading = false

  protected reviewsList: Review[] = []

  @MobileFiltersModule.Getter('GET_FILTERS')
  readonly mobileFilters!: AmpMobileFiltersState

  @HomeFiltersModule.Getter('GET_FILTERS')
  readonly homeFilters!: AmpHomeFiltersState

  async asyncData (ctx: Context): Promise<AsyncComponentData | void> {
    const {
      redirect,
      localePath,
      params: {
        slug,
      },
    } = ctx

    try {
      const provider = await $api.OfferService.getProviderBySlug(slug)
      const contentArticles = await $api.OfferService.getProviderContentBySlug(slug)

      return {
        slug,
        provider,
        contentArticles,
      }
    } catch (e:any) {
      redirect(301, localePath({
        name: 'compare-mobile',
      }))
    }
  }

  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.seoTitle,
        description: this.seoDescription,
        keywords: this.seoKeywords,
        route: { name: 'provider-slug', params: { slug: this.$route.params.slug } },
        customJSONLD: this.schemaMarkup ? [this.schemaMarkup] : [],
        isExcludeOrgRating: true,
      },
    )
  }

  get providersListWithoutCurrent () {
    return this.providersList.filter((p: Provider) => {
      return p.pk !== this.provider.pk
    })
  }

  get reviewsPerPage () {
    if (!process.browser) {
      return 4
    }

    if (window.matchMedia('(max-width: 575px)').matches) {
      return 1
    }

    if (window.matchMedia('(max-width: 767px)').matches) {
      return 2
    }

    if (window.matchMedia('(max-width: 991px)').matches) {
      return 3
    }

    return 4
  }

  get pageTitle () {
    return this.provider?.seo?.title_h1 || ''
  }

  get seoTitle () {
    return this.provider?.seo?.title || ''
  }

  get seoDescription () {
    return this.provider?.seo?.meta_description || ''
  }

  get seoKeywords () {
    return this.provider?.seo?.meta_keywords || ''
  }

  get content () {
    return this.contentArticles.map((article: any) => {
      return {
        ...article,
        slug: article.title.toLowerCase().replace(/[^a-zA-Z]+/g, '-'),
      }
    })
  }

  get tableOfContents () {
    return this.content.map((article: any) => {
      return {
        title: article.title,
        href: `#${article.slug}`,
      }
    })
  }

  get checkAvailabilityLink () {
    return this.localePath({
      name: 'coverage',
      query: {
        provider: this.provider.slug,
      },
    })
  }

  get reviewsPagesAvailable () {
    return Math.ceil(this.reviewsCount / this.reviewsPerPage)
  }

  get schemaMarkup () {
    if (!this.provider.seo || !this.provider.seo.json_ld_markup) {
      return null
    }

    const schema = { ...this.provider.seo.json_ld_markup }

    if (!schema.aggregateRating) {
      schema.aggregateRating = {}
    }

    schema.image = this.provider.logo || schema.image
    schema.aggregateRating.ratingValue = schema.aggregateRating.ratingValue || String(Math.ceil(this.provider.avg_score))
    schema.aggregateRating.reviewCount = schema.aggregateRating.reviewCount || String(this.provider.feedback_set_count)

    return schema
  }

  onRatingCarouselCTAClick (slug: string) {
    this.$router.push(this.localePath({
      name: 'provider-slug',
      params: {
        slug,
      },
    }))
  }

  setOfferType (offerType: OfferTypeKey) {
    this.offerType = offerType
    this.$router.push({ path: this.$route.path, query: { offer_type: offerType } })
  }

  scrollTo (hash: string) {
    const element = document.querySelector(hash)

    if (!element) {
      return
    }

    this.$scrollTo(element, 200, {
      offset: -80,
    })
  }

  onPaginationChange (page: number) {
    this.reviewsPage = page
    this.fetchReviewsList()
  }

  onReviewsFilterCityChange (val: string) {
    this.reviewsFilterForm.city = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  onReviewsFilterAgeGroupChange (val: number) {
    this.reviewsFilterForm.ageGroup = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  onReviewsFilterRatingChange (val: number) {
    this.reviewsFilterForm.rating = val
    this.resetPage()
    this.debouncedFetchReviews()
  }

  resetPage () {
    this.reviewsPage = 1
  }

  debouncedFetchReviews () {
    if (debounce) {
      clearTimeout(debounce)
    }
    debounce = setTimeout(this.fetchReviewsList, 400)
  }

  async fetchReviewsList () {
    let loaderDebounce: any = null

    try {
      if (this.reviewsPage === 1) {
        // Prevent flicker
        loaderDebounce = setTimeout(() => {
          this.isReviewsFullLoading = true
        }, 400)
      } else {
        this.isReviewsPartLoading = true
      }

      const { city, rating, ageGroup } = this.reviewsFilterForm
      const { results, count } = await $api.RatingService.getReviewsList({
        provider_id: this.provider.pk,
        limit: this.reviewsPerPage,
        offset: (this.reviewsPage * this.reviewsPerPage) - this.reviewsPerPage,
        age_group: ageGroup === 0 ? '' : ageGroup,
        city,
        rating,
      })
      this.reviewsList = results
      this.reviewsCount = count
    } catch (e) {
      // TODO: Handle error
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      if (loaderDebounce) {
        clearTimeout(loaderDebounce)
      }

      this.isReviewsFullLoading = false
      this.isReviewsPartLoading = false
    }
  }

  created () {
    const offerType = this.$route.query.offer_type as string

    if (offerType && offerType in OfferType) {
      this.setOfferType(offerType as OfferTypeByShortName)
    }
  }

  mounted () {
    this.$analytics.GTM.sendPageViewEvent({
      routeName: this.$route.name as string,
      pageTitle: this.$metaInfo.title || '',
      fullPath: this.$route.fullPath,
      fromPath: this.$nuxt.context.from ? this.$nuxt.context.from.fullPath : '',
      contentGroup: 'Plan Provider',
    })

    this.fetchReviewsList()
  }

  @MobileFiltersModule.Mutation('SET_FILTER')
  setMobileilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  @HomeFiltersModule.Mutation('SET_FILTER')
  setHomeFilters!: ({
    value,
    key,
  }: { value: FiltersItemValue, key: string }) => void

  destroyed () {
    this.setMobileilters({
      key: 'providers',
      value: [],
    })
    this.setHomeFilters({
      key: 'providers',
      value: [],
    })
  }
}
